// Vista del formulario dinámico (app-form-component): a partir de la configuración
// del paso (IInputForm[], la que arma el constructor de campos del panel) decide
// cómo se pinta cada campo —teclado, máscara, ayuda, ícono del tema— sin tocar
// los validadores, que siguen saliendo de generateForm() en el componente.
import {
  faAddressBook,
  faBriefcase,
  faBuildingColumns,
  faClipboardList,
  faHouse,
  faIdCard,
  faLandmarkFlag,
  faUserGroup,
  faWallet,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons";
import {IInputForm} from "../../../../../data/interfaces/iinput-form.interface";

// Cómo se pinta el campo:
//  texto     <input> normal (en mayúsculas, como siempre)
//  telefono  p-inputMask «55 1234 5678» (patrón phone52, 10 dígitos)
//  correo    <input type="email">
//  clabe     <input inputmode="numeric"> 18 dígitos con contador
//  curp      <input> 18 caracteres con contador
//  numerico  <input inputmode="numeric"> (patrón propio solo de dígitos: CP, etc.)
//  numero    p-inputNumber
//  moneda    p-inputNumber en pesos
//  fecha     p-calendar dd/mm/aaaa
//  lista     p-dropdown (4 opciones o más)
//  opciones  tarjetas de radio (hasta 3 opciones: Sí/No, tipo de contrato…)
export type TipoCampo = 'texto' | 'telefono' | 'correo' | 'clabe' | 'curp' | 'numerico' | 'numero' | 'moneda' | 'fecha' | 'lista' | 'opciones'

export interface IOpcionCampo {
  label: string
  value: string
}

export interface ICampoVista {
  // La configuración tal cual (shouldShowField y los validadores la usan).
  input: IInputForm
  // Nombre del FormControl (input.id).
  id: string
  // id del control en el DOM (prefijado y sin caracteres raros).
  domId: string
  etiqueta: string
  // Tema al que pertenece (título del SEPARATOR), para «Siguiente: Celular · Referencia laboral».
  grupo: string
  placeholder: string
  tipo: TipoCampo
  // Tiene un validador que generateForm convierte en Validators.required.
  obligatorio: boolean
  // Tope de caracteres (CLABE, CURP, dígitos fijos); null = sin tope.
  maxLength: number | null
  // Muestra «12/18» bajo el campo.
  contador: boolean
  // Se le aplica validateInput() (mayúsculas), como a todo INPUT_TEXT.
  mayusculas: boolean
  // Al escribir se quitan espacios y guiones (CLABE, CP…).
  soloDigitos: boolean
  // Cómo llenarlo («A 10 dígitos, sin lada»), siempre visible.
  ayuda: string
  tipoHtml: 'text' | 'tel' | 'email'
  inputmode: string | null
  autocomplete: string
  autocapitalize: string
  // Referencia estable para [options] (nunca un getter).
  opciones: IOpcionCampo[]
  // Opciones cortas (Sí/No) lado a lado.
  dosColumnas: boolean
  // Buscador en listas largas (estados, bancos…).
  filtro: boolean
}

export interface IGrupoVista {
  clave: string
  titulo: string
  subtitulo: string
  icono: IconDefinition
  campos: ICampoVista[]
}

// Mismas llaves que FormComponentComponent.patterns y el panel (phone52, curp, clabe, email).
const PATRONES_NOMBRADOS = ['phone52', 'curp', 'clabe', 'email']

// Tipos de validación que generateForm NO convierte en required.
const NO_OBLIGATORIAS = ['pattern', 'minlength']

// Teléfonos de referencias que el backend exige distintos entre sí y al del usuario
// (validatePhonesReferences en pulpi-v4-app-backend).
export const TELEFONOS_REFERENCIA = ['personal_reference_phone', 'work_reference_phone', 'other_reference_phone']

export const normalizar = (texto: string | undefined | null): string =>
  (texto ?? '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

export const esObligatorio = (input: IInputForm): boolean =>
  (input.validations ?? []).some(v => !NO_OBLIGATORIAS.includes(v.type))

// «^\d{5}$», «^[0-9]{5}$», «^\d{1,3}$», «^\d+$» → solo dígitos (con su tope si lo hay).
export const digitosDelPatron = (patron: string): {soloDigitos: boolean, maximo: number | null, exacto: boolean} => {
  const m = /^\^?(?:\\d|\[0-9\])(?:\{(\d+)(?:,(\d*))?\}|(\+|\*))\$?$/.exec((patron ?? '').trim())
  if (!m) return {soloDigitos: false, maximo: null, exacto: false}
  if (m[3]) return {soloDigitos: true, maximo: null, exacto: false}
  const minimo = Number(m[1])
  if (m[2] === undefined) return {soloDigitos: true, maximo: minimo, exacto: true}
  return {soloDigitos: true, maximo: m[2] === '' ? null : Number(m[2]), exacto: false}
}

const ICONOS_TEMA: [RegExp, IconDefinition][] = [
  [/laboral|trabajo|empleo|empresa|ocupaci|puesto/, faBriefcase],
  [/refer|familiar|aval|conyuge|beneficiari/, faUserGroup],
  [/contacto|localiz/, faAddressBook],
  [/domicilio|direcci|vivienda|colonia|casa/, faHouse],
  [/banc|cuenta|clabe|tarjeta/, faBuildingColumns],
  [/ingreso|gasto|econom|financ|sueldo|salario|dinero/, faWallet],
  [/public|pep|politic/, faLandmarkFlag],
  [/personal|general|identific|tus datos|datos del/, faIdCard]
]

export const iconoDeTema = (texto: string): IconDefinition => {
  const t = normalizar(texto)
  return ICONOS_TEMA.find(([patron]) => patron.test(t))?.[1] ?? faClipboardList
}

const VERBOS_INICIALES = /^(completa|llena|captura|ingresa|registra|agrega|actualiza|confirma|proporciona|danos|déjanos|dejanos|cuéntanos|cuentanos)\s+/i

const minusculaInicial = (texto: string): string => {
  if (texto.length > 1 && texto[1] === texto[1].toLowerCase() && texto[1] !== texto[1].toUpperCase()) {
    return texto[0].toLowerCase() + texto.slice(1)
  }
  return texto
}

// El botón dice qué pasa: «Datos laborales» → «Guardar datos laborales».
export const textoGuardar = (titulo: string | undefined | null): string => {
  const base = minusculaInicial((titulo ?? '').trim().replace(VERBOS_INICIALES, ''))
  if (!base || base.length > 32) return 'Guardar mi información'
  return 'Guardar ' + base
}

// «8 datos · 2 min aprox.»
export const textoDuracion = (campos: number): string => {
  const minutos = Math.max(1, Math.round(campos * 12 / 60))
  return `${campos} ${campos === 1 ? 'dato' : 'datos'} · ${minutos} min aprox.`
}

const idSeguro = (id: string): string => 'lxb-' + (id ?? '').toString().replace(/[^a-zA-Z0-9_-]/g, '_')

const patronDe = (input: IInputForm): string => {
  const v = (input.validations ?? []).find(x => x.type === 'pattern')
  return v ? (v.value ?? '').toString() : ''
}

const minimoDe = (input: IInputForm): number | null => {
  const v = (input.validations ?? []).find(x => x.type === 'minlength')
  return v ? Number(v.value) || null : null
}

export const armarCampo = (input: IInputForm, tituloGrupo = ''): ICampoVista => {
  const patron = patronDe(input)
  const nombre = normalizar(`${input.id} ${input.nameField}`)
  const deReferencia = /refer|aval|conyuge/.test(normalizar(`${input.id} ${input.nameField} ${tituloGrupo}`))
  const opciones: IOpcionCampo[] = Array.isArray(input.options) ? input.options : []
  const campo: ICampoVista = {
    input,
    id: input.id,
    domId: idSeguro(input.id),
    etiqueta: (input.nameField ?? '').trim(),
    grupo: tituloGrupo,
    placeholder: input.placeholder ?? '',
    tipo: 'texto',
    obligatorio: esObligatorio(input),
    maxLength: null,
    contador: false,
    mayusculas: false,
    soloDigitos: false,
    ayuda: '',
    tipoHtml: 'text',
    inputmode: null,
    autocomplete: 'off',
    autocapitalize: 'off',
    opciones,
    dosColumnas: false,
    filtro: false
  }

  switch (input.typeField) {
    case 'SELECT':
      campo.tipo = opciones.length > 0 && opciones.length <= 3 ? 'opciones' : 'lista'
      campo.dosColumnas = campo.tipo === 'opciones' && opciones.length === 2 && opciones.every(o => (o.label ?? '').length <= 14)
      campo.filtro = opciones.length > 8
      return campo
    case 'INPUT_DATE':
      campo.tipo = 'fecha'
      return campo
    case 'INPUT_NUMBER':
      campo.tipo = /monto|ingreso|sueldo|salario|renta|gasto|pago|importe|precio|ahorro/.test(nombre) ? 'moneda' : 'numero'
      return campo
  }

  // INPUT_TEXT (y cualquier tipo de texto que llegue): mayúsculas como siempre.
  campo.mayusculas = true
  campo.autocapitalize = 'characters'
  const digitos = digitosDelPatron(patron)
  const minimo = minimoDe(input)
  if (patron === 'phone52') {
    campo.tipo = 'telefono'
    campo.mayusculas = false
    campo.tipoHtml = 'tel'
    campo.inputmode = 'tel'
    campo.autocomplete = deReferencia ? 'off' : 'tel-national'
    campo.placeholder = campo.placeholder || '55 1234 5678'
    campo.ayuda = 'A 10 dígitos, sin lada internacional.'
  } else if (patron === 'email' || (!patron && /correo|email|e-mail/.test(nombre))) {
    campo.tipo = 'correo'
    campo.tipoHtml = 'email'
    campo.inputmode = 'email'
    campo.autocomplete = deReferencia ? 'off' : 'email'
    campo.autocapitalize = 'off'
    campo.placeholder = campo.placeholder || 'nombre@correo.com'
  } else if (patron === 'clabe') {
    campo.tipo = 'clabe'
    campo.inputmode = 'numeric'
    campo.maxLength = 18
    campo.contador = true
    campo.soloDigitos = true
    campo.placeholder = campo.placeholder || '18 dígitos'
    campo.ayuda = 'Está en tu estado de cuenta o en la app de tu banco.'
  } else if (patron === 'curp') {
    campo.tipo = 'curp'
    campo.maxLength = 18
    campo.contador = true
    campo.ayuda = '18 letras y números. Viene en tu INE o en tu acta de nacimiento.'
  } else if (digitos.soloDigitos) {
    campo.tipo = 'numerico'
    campo.inputmode = 'numeric'
    campo.soloDigitos = true
    campo.maxLength = digitos.maximo
    campo.contador = digitos.exacto && !!digitos.maximo
    if (/postal|^cp\b|\bcp\b|zip/.test(nombre) && !deReferencia) campo.autocomplete = 'postal-code'
    campo.ayuda = digitos.exacto && digitos.maximo ? `Solo números: son ${digitos.maximo} dígitos.`
      : digitos.maximo ? `Solo números, hasta ${digitos.maximo} dígitos.` : 'Solo números.'
  } else if (!patron && /telefono|celular|phone|tel\b/.test(nombre)) {
    campo.tipo = 'texto'
    campo.tipoHtml = 'tel'
    campo.inputmode = 'tel'
  }
  if (minimo && !campo.ayuda) campo.ayuda = `Escribe al menos ${minimo} caracteres.`
  if (PATRONES_NOMBRADOS.includes(patron) || digitos.soloDigitos) campo.autocapitalize = campo.tipo === 'curp' ? 'characters' : 'off'
  return campo
}

// Los SEPARATOR abren un tema nuevo; lo que va antes del primero queda en un grupo sin título.
export const armarGrupos = (inputs: IInputForm[]): IGrupoVista[] => {
  const grupos: IGrupoVista[] = []
  let actual: IGrupoVista = {clave: 'g0', titulo: '', subtitulo: '', icono: faClipboardList, campos: []}
  for (const input of inputs ?? []) {
    if (input.typeField === 'SEPARATOR') {
      if (actual.campos.length) grupos.push(actual)
      const titulo = (input.title ?? '').trim()
      actual = {clave: 'g' + (grupos.length + 1), titulo, subtitulo: (input.subtitle ?? '').trim(), icono: iconoDeTema(titulo), campos: []}
      continue
    }
    actual.campos.push(armarCampo(input, actual.titulo))
  }
  if (actual.campos.length) grupos.push(actual)
  return grupos
}
